import { isPlatformBrowser } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { EncryptionService } from '../services/encryption.service';
import { environment } from 'environments/environment';

type AngularResponseType = 'arraybuffer' | 'blob' | 'json' | 'text';

interface PreparedBody {
  body: ArrayBuffer | null;
  contentType?: string;
}

const SKIP_ENCRYPTION_HEADER = 'X-Skip-Encryption';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly encryptionService: EncryptionService,
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return from(this.encryptionService.ensureConfigLoaded()).pipe(
      switchMap(() => {
        if (!this.shouldEncrypt(req)) {
          return next.handle(this.removeSkipHeader(req));
        }

        const originalResponseType = req.responseType as AngularResponseType;

        return from(this.prepareBody(req)).pipe(
          switchMap((preparedBody) =>
            from(this.encryptionService.encryptRequest(preparedBody.body)).pipe(
              switchMap((encryptedPayload) => {
                const encryptedReq = this.buildEncryptedRequest(
                  req,
                  encryptedPayload.body,
                  encryptedPayload.headers,
                  preparedBody.contentType,
                );

                return next.handle(encryptedReq).pipe(
                  mergeMap((event) =>
                    from(
                      this.decryptHttpEvent(
                        event,
                        encryptedPayload.aesKey,
                        originalResponseType,
                      ),
                    ),
                  ),
                  catchError((error) =>
                    from(
                      this.decryptHttpError(
                        error,
                        encryptedPayload.aesKey,
                        originalResponseType,
                      ),
                    ).pipe(
                      switchMap((normalizedError) =>
                        throwError(() => normalizedError),
                      ),
                    ),
                  ),
                );
              }),
            ),
          ),
        );
      }),
    );
  }

  /**
   * Option B — Secure-by-default GET encryption (server-driven).
   *
   * POST / PUT / PATCH : always encrypted.
   * GET               : encrypted for ALL paths EXCEPT those returned by
   *                     GET /key-exchange/config (fetched on first use).
   * HEAD / DELETE / OPTIONS : never encrypted.
   *
   * The skiplist is owned by the backend — no frontend code change needed
   * when adding or removing reference-data endpoints.
   */
  private shouldEncrypt(req: HttpRequest<unknown>): boolean {
    if (!this.encryptionService.isEnabled()) {
      return false;
    }

    if (
      !isPlatformBrowser(this.platformId) ||
      !this.encryptionService.isSupported()
    ) {
      return false;
    }

    if (req.headers.has(SKIP_ENCRYPTION_HEADER)) {
      return false;
    }

    const apiBaseUrl = environment.apiURL.replace(/\/$/, '');
    if (!req.url.startsWith(apiBaseUrl) || req.url.includes('/key-exchange/')) {
      return false;
    }

    const method = req.method.toUpperCase();

    // POST / PUT / PATCH — always encrypt (carry a request body)
    if (['POST', 'PUT', 'PATCH'].includes(method)) {
      return true;
    }

    // GET — encrypt everything EXCEPT paths the server marks as non-sensitive
    if (method === 'GET') {
      return !this.encryptionService.isSkippedGetPath(req.url);
    }

    // HEAD / DELETE / OPTIONS — no sensitive payload, skip
    return false;
  }


  private removeSkipHeader(req: HttpRequest<unknown>): HttpRequest<unknown> {
    return req.headers.has(SKIP_ENCRYPTION_HEADER)
      ? req.clone({ headers: req.headers.delete(SKIP_ENCRYPTION_HEADER) })
      : req;
  }

  private async prepareBody(req: HttpRequest<unknown>): Promise<PreparedBody> {
    if (req.body === null || req.body === undefined) {
      return { body: null };
    }

    if (req.body instanceof ArrayBuffer) {
      return {
        body: req.body,
        contentType:
          req.headers.get('Content-Type') || 'application/octet-stream',
      };
    }

    if (req.body instanceof Blob) {
      return {
        body: await req.body.arrayBuffer(),
        contentType:
          req.headers.get('Content-Type') ||
          req.body.type ||
          'application/octet-stream',
      };
    }

    if (req.body instanceof FormData) {
      const formRequest = new Request('https://dms.local', {
        method: req.method,
        body: req.body,
      });

      return {
        body: await formRequest.arrayBuffer(),
        contentType:
          req.headers.get('Content-Type') ||
          formRequest.headers.get('Content-Type') ||
          undefined,
      };
    }

    const contentType = req.headers.get('Content-Type') || 'application/json';
    const body =
      typeof req.body === 'string'
        ? req.body
        : contentType.includes('application/x-www-form-urlencoded')
          ? String(req.body)
          : JSON.stringify(req.body);

    return {
      body: new TextEncoder().encode(body).buffer,
      contentType,
    };
  }

  private buildEncryptedRequest(
    req: HttpRequest<unknown>,
    encryptedBody: ArrayBuffer | null,
    encryptionHeaders: Record<string, string>,
    originalContentType?: string,
  ): HttpRequest<unknown> {
    let headers = req.headers.delete(SKIP_ENCRYPTION_HEADER);

    Object.entries(encryptionHeaders).forEach(([name, value]) => {
      headers = headers.set(name, value);
    });

    if (encryptedBody && originalContentType) {
      headers = headers.set('Content-Type', originalContentType);
    } else if (encryptedBody) {
      headers = headers.set('Content-Type', 'application/octet-stream');
    }

    return req.clone({
      body: encryptedBody,
      headers,
      responseType: 'arraybuffer' as 'json',
    });
  }

  private async decryptHttpEvent(
    event: HttpEvent<unknown>,
    aesKey: CryptoKey,
    originalResponseType: AngularResponseType,
  ): Promise<HttpEvent<unknown>> {
    if (
      !(event instanceof HttpResponse) ||
      !(event.body instanceof ArrayBuffer)
    ) {
      return event;
    }

    const responseNonce = event.headers.get('X-Response-Nonce');
    const plainBody = responseNonce
      ? await this.encryptionService.decryptResponse(
          aesKey,
          responseNonce,
          event.body,
        )
      : event.body;

    return event.clone({
      body: this.deserializeBody(
        plainBody,
        originalResponseType,
        event.headers,
      ),
    });
  }

  private async decryptHttpError(
    error: unknown,
    aesKey: CryptoKey,
    originalResponseType: AngularResponseType,
  ): Promise<unknown> {
    if (!(error instanceof HttpErrorResponse)) {
      return error;
    }

    if (!(error.error instanceof ArrayBuffer)) {
      return error;
    }

    const responseNonce = error.headers?.get('X-Response-Nonce');
    let bodyBuffer = error.error;

    if (responseNonce) {
      try {
        bodyBuffer = await this.encryptionService.decryptResponse(
          aesKey,
          responseNonce,
          error.error,
        );
      } catch {
        this.encryptionService.clearServerPublicKey();
        return error;
      }
    }

    return new HttpErrorResponse({
      error: this.deserializeBody(
        bodyBuffer,
        originalResponseType,
        error.headers,
      ),
      headers: error.headers,
      status: error.status,
      statusText: error.statusText,
      url: error.url || undefined,
    });
  }

  private deserializeBody(
    body: ArrayBuffer,
    responseType: AngularResponseType,
    headers: HttpHeaders,
  ): unknown {
    if (responseType === 'arraybuffer') {
      return body;
    }

    if (responseType === 'blob') {
      return new Blob([body], {
        type: headers.get('Content-Type') || 'application/octet-stream',
      });
    }

    const text = new TextDecoder().decode(body);

    if (responseType === 'text') {
      return text;
    }

    if (!text) {
      return null;
    }

    return JSON.parse(text);
  }
}
